import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api.js';
import PageHeader from '../../components/PageHeader.jsx';
import { Icon, SECTION_COLOR } from '../../icons.jsx';
import LocationFormModal from './LocationFormModal.jsx';
import { displayPhone } from '../../phone.js';

const AVATAR_COLORS = ['#F2A93B', '#2DC4C4', '#3FBF6F', '#9B84E8', '#E86E96', '#4C8FE0', '#E0475E', '#94A3B8'];

function initialsAvatar(name) {
  const initials = (name || '').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = (name.charCodeAt(i) + ((hash << 5) - hash));
  const color = AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  return { initials, color };
}

const TrashIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>;

const TOOLBAR_ACTIONS = [
  { label: 'Print', icon: Icon.filePlus, disabled: true },
  { label: 'Download', icon: Icon.upload, disabled: true },
  { label: 'Active Columns', icon: Icon.columns, disabled: true }
];

export default function LocationList() {
  const [locations, setLocations] = useState(null);
  const [query, setQuery] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  function load() {
    setLocations(null);
    api('/dispatch/locations').then(setLocations).catch(() => setLocations([]));
  }

  const deleteLocation = async (e, l) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${l.name}"? This can't be undone.`)) return;
    try {
      await api(`/dispatch/locations/${l.id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!locations) return [];
    const q = query.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter(l =>
      [l.name, l.ref_number, l.city, l.contact_email, l.location_type]
        .filter(Boolean).some(v => v.toLowerCase().includes(q)));
  }, [locations, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <PageHeader
        icon={Icon.control}
        title="Locations"
        subtitle="Pickup and delivery locations used across orders"
        color={SECTION_COLOR.control}
        actions={
          <>
            <button className="primary-btn" onClick={() => { setEditing(null); setFormOpen(true); }}>+ New location</button>
            {TOOLBAR_ACTIONS.map(a => (
              <button key={a.label} className="toolbar-btn" disabled={a.disabled} title={`${a.label} — coming soon`}>
                <a.icon />
                <span>{a.label}</span>
                <span className="nav-flyout-soon">Soon</span>
              </button>
            ))}
            <button className="topbar-icon-btn" title="Search"><Icon.search /></button>
          </>
        }
      />

      <div className="panel filter-row-panel">
        <div className="filter-row" style={{ justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input placeholder="Type to filter..." value={query}
              onChange={e => { setQuery(e.target.value); setPage(1); }}
              style={{ width: 240 }} />
            <button className="topbar-icon-btn" title="Search"><Icon.search /></button>
          </div>
          <label style={{ fontSize: 13, color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 6 }}>
            Show:
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} style={{ width: 70 }}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </label>
        </div>
      </div>

      {locations === null && <div className="empty">Loading...</div>}
      {locations && locations.length === 0 && <div className="empty">No locations yet — click "+ New location" above.</div>}

      {locations && locations.length > 0 && (
        <>
          <table>
            <thead>
              <tr>
                <th>Ref#</th>
                <th>Name</th>
                <th>Type</th>
                <th>Address</th>
                <th>Email ID</th>
                <th>Phone#</th>
                <th>Fax#</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(l => {
                const avatar = initialsAvatar(l.name);
                return (
                <tr key={l.id} onClick={() => { setEditing(l); setFormOpen(true); }} style={{ cursor: 'pointer' }}>
                  <td>{l.ref_number || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{
                        display: 'inline-flex', width: 30, height: 30, borderRadius: '50%',
                        background: avatar.color, color: '#fff', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, flexShrink: 0
                      }}>{avatar.initials}</span>
                      <div>{l.name}</div>
                    </div>
                  </td>
                  <td>{l.location_type || '—'}</td>
                  <td>
                    {l.address && <div>{l.address}</div>}
                    <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>
                      {[l.city, l.province, l.postal_code].filter(Boolean).join(', ')}
                      {l.country ? `, ${l.country}` : ''}
                    </div>
                  </td>
                  <td>{l.contact_email || '—'}</td>
                  <td>{displayPhone(l.phone) || '—'}</td>
                  <td>{displayPhone(l.fax) || '—'}</td>
                  <td>{l.status || 'Active'}</td>
                  <td>
                    <button type="button" title="Delete location" onClick={e => deleteLocation(e, l)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 30, height: 30, border: '1px solid var(--border)', background: 'var(--surface)',
                        color: 'var(--red)', cursor: 'pointer', padding: 0
                      }}>
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, fontSize: 13, color: 'var(--text-dim)' }}>
            <span>Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, filtered.length)} of {filtered.length} entries</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button className="page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
              <span className="page-btn" style={{ background: 'var(--blue)', color: '#fff', cursor: 'default' }}>{page}</span>
              <button className="page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>→</button>
            </div>
          </div>
        </>
      )}

      {formOpen && (
        <LocationFormModal
          location={editing}
          allLocations={locations || []}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); load(); }}
        />
      )}
    </div>
  );
}
